export const getId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
};

export const isSalesRole = (role) => role === "sales";

export const getReportingManagerId = (user) => getId(user?.reportingManager);

export const getRoleFlags = (user = {}) => ({
  loggedInId: getId(user),
  isAdmin: user.role === "admin",
  isSeniorManager: user.role === "senior-manager",
  isManager: user.role === "manager",
  isSales: user.role === "sales",
});

export const getHierarchyUserIds = (users = [], loggedInUser = {}) => {
  const loggedInId = getId(loggedInUser);

  const ids = [loggedInId];

  const findChildren = (parentId) => {
    users.forEach((user) => {
      const userId = getId(user);
      const managerId = getId(user.reportingManager);

      if (managerId === parentId && !ids.includes(userId)) {
        ids.push(userId);
        findChildren(userId);
      }
    });
  };

  findChildren(loggedInId);

  return ids;
};

export const getSalesUnderUser = (users = [], userId) => {
  return users.filter(
    (user) => user.role === "sales" && getReportingManagerId(user) === userId,
  );
};

// export const buildVisibleOperatorsByRole = (
//   operators = [],
//   users = [],
//   loggedInUser = {},
// ) => {
//   const roleFlags = getRoleFlags(loggedInUser);

//   if (roleFlags.isAdmin) return operators;

//   const allowedUserIds = getHierarchyUserIds(users, loggedInUser);

//   return operators.filter((operator) => {
//     const createdById = getId(operator.createdBy);
//     const assignedToId = getId(operator.assignedTo);

//     return (
//       allowedUserIds.includes(createdById) ||
//       allowedUserIds.includes(assignedToId)
//     );
//   });
// };

export const buildVisibleOperatorsByRole = (
  operators = [],
  users = [],
  loggedInUser = {},
) => {
  return operators;
};

export const getManagersUnderUser = (users = [], userId) => {
  return users.filter(
    (user) =>
      user.role === "manager" && getReportingManagerId(user) === String(userId),
  );
};

export const buildVisibleUsersByRole = (users = [], loggedInUser = {}) => {
  const { loggedInId, isAdmin, isSeniorManager, isManager } =
    getRoleFlags(loggedInUser);

  if (isAdmin) return users;

  if (isSeniorManager) {
    const managers = getManagersUnderUser(users, loggedInId);
    const managerIds = managers.map((manager) => getId(manager));

    return users.filter((user) => {
      const reportingManagerId = getReportingManagerId(user);

      return (
        (user.role === "manager" && reportingManagerId === loggedInId) ||
        (user.role === "sales" &&
          (reportingManagerId === loggedInId ||
            managerIds.includes(reportingManagerId)))
      );
    });
  }

  if (isManager) {
    return users.filter(
      (user) =>
        user.role === "sales" && getReportingManagerId(user) === loggedInId,
    );
  }

  return [];
};

export const canEditByHierarchy = (
  targetUser,
  users = [],
  loggedInUser = {},
) => {
  const { loggedInId, isAdmin, isSeniorManager, isManager } =
    getRoleFlags(loggedInUser);

  if (isAdmin) return true;

  const reportingManagerId = getReportingManagerId(targetUser);

  if (isSeniorManager) {
    const managers = getManagersUnderUser(users, loggedInId);
    const managerIds = managers.map((manager) => getId(manager));

    return (
      (targetUser.role === "manager" && reportingManagerId === loggedInId) ||
      (targetUser.role === "sales" &&
        (reportingManagerId === loggedInId ||
          managerIds.includes(reportingManagerId)))
    );
  }

  if (isManager) {
    return targetUser.role === "sales" && reportingManagerId === loggedInId;
  }

  return false;
};

export const buildVisibleLeadsByRole = (
  leads = [],
  users = [],
  loggedInUser = {},
) => {
  const { loggedInId, isAdmin } = getRoleFlags(loggedInUser);

  if (isAdmin) return leads;

  const allowedUserIds = getHierarchyUserIds(users, loggedInUser).map(String);

  return leads.filter((lead) => {
    const assignedTo = lead.assignedTo;
    const assignedToKeys = [
      getId(assignedTo),
      assignedTo?._id,
      assignedTo?.id,
      assignedTo?.userId,
      assignedTo?.email,
      assignedTo?.name,
    ]
      .filter(Boolean)
      .map(String);

    return assignedToKeys.some((key) => allowedUserIds.includes(key));
  });
};

export const buildVisibleDemosByRole = (
  demos = [],
  operators = [],
  users = [],
  loggedInUser = {},
) => {
  const { loggedInId, isAdmin } = getRoleFlags(loggedInUser);

  if (isAdmin) return demos;

  const allowedUserIds = getHierarchyUserIds(users, loggedInUser).map(String);

  // Create a map of operator IDs to their assigned user IDs
  const operatorToUserMap = {};
  operators.forEach((operator) => {
    const operatorId = getId(operator);
    const assignedTo = operator.assignedTo;
    const assignedToKeys = [
      getId(assignedTo),
      assignedTo?._id,
      assignedTo?.id,
      assignedTo?.userId,
      assignedTo?.email,
      assignedTo?.name,
    ]
      .filter(Boolean)
      .map(String);

    operatorToUserMap[operatorId] = assignedToKeys;
  });

  return demos.filter((demo) => {
    const operatorId = getId(demo.operatorId);
    const assignedToKeys = operatorToUserMap[operatorId] || [];

    return assignedToKeys.some((key) => allowedUserIds.includes(key));
  });
};
